import React, { useEffect, useState, useCallback } from 'react';
import { Button, ButtonGroup } from 'react-bootstrap';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import PropTypes from 'prop-types';
import RadioForm from './RadioForm';
import GoogleAnalyticsForm from './GoogleAnalyticsForm';
import AcutradeForm from './AcutradeForm';
import groupActions from '../../../../redux/groups/actions';

export const leadFormOptions = [
  {
    label: 'First Step',
    value: 1,
    defaultValue: true,
  },
  {
    label: 'Second Step',
    value: 2,
  },
  {
    label: 'PII in Flow',
    value: -1,
  },
];

const splashFormOptions = [
  {
    label: 'Yes, display alternate text',
    value: true,
  },
  {
    label: 'No, default text is fine',
    value: false,
  },
];

function Config({ history, fetching, actions, group }) {
  const [piiFlow, setPiiFlow] = useState(1);
  const [alternateHeaders, setAlternateHeaders] = useState(false);
  const [showPaymentSummary, setShowPaymentSummary] = useState();
  const [googleAnalytics, setGoogleAnalytics] = useState();
  const [googleAnalyticsTrackingId, setGoogleAnalyticsTrackingId] = useState();
  const [acuTrade, setAcuTrade] = useState();
  const [acuTradeDealerId, setAcuTradeDealerId] = useState('');
  const [acuTradeApiKey, setAcuTradeApiKey] = useState('');
  const [acuTradeShowKbb, setAcuTradeShowKbb] = useState(false);

  const onCancel = useCallback(() => history.push('/groups'), [history]);

  useEffect(() => {
    const { config } = group || {};
    const { featureConfig, wizard } = config || {};
    const {
      leadForm,
      splashPage,
      paymentBuilder,
      googleAnalytics: _googleAnalytics,
    } = wizard || {};
    const { acuTrade: _acuTrade } = featureConfig || {};
    const { piiFlow: _piiFlow } = leadForm || { piiFlow: 1 };
    const { alternateHeaders: _alternateHeaders = false } = splashPage || {};
    const { showPaymentSummary: _showPaymentSummary = true } =
      paymentBuilder || {};
    const {
      enabled: googleAnalyticsEnabled,
      trackingId: googleTrackingId,
    } = _googleAnalytics || { enabled: false };
    const {
      enabled: acuTradeEnabled,
      dealerID: _acuTradeDealerId,
      dealerKey: _acuTradeApiKey,
      showKBBOption,
    } = _acuTrade || {};

    setPiiFlow(_piiFlow);
    setAlternateHeaders(_alternateHeaders);
    setShowPaymentSummary(_showPaymentSummary);
    setGoogleAnalytics(googleAnalyticsEnabled);
    setGoogleAnalyticsTrackingId(googleTrackingId);
    setAcuTrade(acuTradeEnabled);
    setAcuTradeDealerId(_acuTradeDealerId);
    setAcuTradeApiKey(_acuTradeApiKey);
    setAcuTradeShowKbb(showKBBOption);
  }, [group]);

  const onSelectPiiFlow = e => {
    setPiiFlow(Number(e.target.value));
  };

  const onSelectAlternateHeaders = useCallback(
    e => {
      if (e.target.value !== alternateHeaders) {
        setAlternateHeaders(!alternateHeaders);
      }
    },
    [alternateHeaders],
  );

  const onToggleGoogleAnalytics = useCallback(() => {
    if (googleAnalytics) {
      setGoogleAnalyticsTrackingId(null);
    }
    setGoogleAnalytics(!googleAnalytics);
  }, [googleAnalytics]);

  const onChangeGoogleTrackingId = useCallback(e => {
    setGoogleAnalyticsTrackingId(e.target.value);
  }, []);

  const onToggleAcuTrade = useCallback(() => {
    if (acuTrade) {
      setAcuTradeDealerId(null);
      setAcuTradeApiKey(null);
      setAcuTradeShowKbb(false);
    }
    setAcuTrade(!acuTrade);
  }, [acuTrade]);

  const onChangeAcuTradeDealer = useCallback(e => {
    setAcuTradeDealerId(e.target.value);
  }, []);

  const onChangeAcuTradeApiKey = useCallback(e => {
    setAcuTradeApiKey(e.target.value);
  }, []);

  const onToggleKbb = useCallback(() => {
    setAcuTradeShowKbb(!acuTradeShowKbb);
  }, [acuTradeShowKbb]);

  const onSubmit = e => {
    e.preventDefault();
    const modifiedConfig = {
      ...group?.config,
    };

    const newAcuTrade = { enabled: acuTrade };
    if (acuTrade) {
      if (acuTradeDealerId) {
        newAcuTrade.dealerID = acuTradeDealerId;
      }
      if (acuTradeApiKey) {
        newAcuTrade.dealerKey = acuTradeApiKey;
      }
      if (acuTradeShowKbb) {
        newAcuTrade.showKBBOption = acuTradeShowKbb;
      }
    }

    modifiedConfig.featureConfig = {
      ...modifiedConfig.featureConfig,
      acuTrade: newAcuTrade,
    };

    const newGoogleAnalytics = { enabled: googleAnalytics };
    if (googleAnalyticsTrackingId) {
      newGoogleAnalytics.trackingId = googleAnalyticsTrackingId;
    }

    modifiedConfig.wizard = {
      ...modifiedConfig.wizard,
      leadForm: { piiFlow },
      splashPage: { alternateHeaders },
      paymentBuilder: { showPaymentSummary },
      googleAnalytics: newGoogleAnalytics,
    };

    actions.updateGroupRequest({
      ...group,
      config: modifiedConfig,
      route: `/groups`,
    });
  };

  return (
    <form onSubmit={onSubmit}>
      <RadioForm
        options={leadFormOptions}
        sectionHeader="Lead Form Config"
        subHeader="PII Workflow"
        description="Require PII at First or Second Step of Workflow"
        selectedValue={piiFlow}
        onSelect={onSelectPiiFlow}
      />
      <RadioForm
        options={splashFormOptions}
        sectionHeader="Splash Page Settings"
        subHeader="Use Alternate Headers"
        description="Display Alternate Lead Capture Form Headers"
        selectedValue={alternateHeaders}
        onSelect={onSelectAlternateHeaders}
      />
      <GoogleAnalyticsForm
        enabled={googleAnalytics}
        onToggle={onToggleGoogleAnalytics}
        trackingId={googleAnalyticsTrackingId}
        onChangeGoogleId={onChangeGoogleTrackingId}
      />
      <AcutradeForm
        enabled={acuTrade}
        acuTradeId={acuTradeDealerId}
        acuTradeApiKey={acuTradeApiKey}
        showKbb={acuTradeShowKbb}
        onToggle={onToggleAcuTrade}
        onChangeAcuTradeId={onChangeAcuTradeDealer}
        onChangeAcuTradeApiKey={onChangeAcuTradeApiKey}
        onToggleKbb={onToggleKbb}
      />
      <div className="setup-spacer" />
      <ButtonGroup>
        <Button onClick={onCancel}>Exit</Button>
        <Button
          bsClass="btn btn-primary btn-large"
          disabled={fetching}
          type="submit"
        >
          Save
        </Button>
      </ButtonGroup>
    </form>
  );
}

Config.propTypes = {
  history: PropTypes.object,
  fetching: PropTypes.bool,
  actions: PropTypes.object,
  group: PropTypes.object,
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ ...groupActions }, dispatch),
});

export default connect(null, mapDispatchToProps)(Config);
